<template>
  <div class="whatsapp-templates">
    <div class="whatsapp-templates__heading">
      <h2>Chat Templates</h2>
    </div>
    <div class="whatsapp-templates__content">
      <h5 class="mt-3">How to chat to guests: 2 scenarios</h5>
      <ol>
        <li>If a <b>guest messages you</b> - you can respond freely for 24 hours</li>
        <li>If <b>you</b> want to reach out to the <b>guest first</b> - you need to <b>create a template</b> and then <b>submit it for WhatsApp approval</b>.<br>Once the template is approved, you can use it in an <b>automation</b>.</li>
      </ol> 
      <h5 class="mt-3">Conversations with Guests</h5>
      <ul>
        <li>
          WhatsApp defines a <strong>conversation</strong> as a 24-hour message thread between you and a guest.
        </li>
        <li>
          You will either be <strong>starting a conversation</strong>
          with a guest, or <strong>replying</strong> to a guest.
        </li>
        <li>
          You <strong>don't need to submit</strong> a template for approval if you will <strong>only</strong> use it to <strong>reply</strong> to a guest.
        </li>
      </ul>

      <div class="whatsapp-templates__table">
        <div class="whatsapp-templates__table__header">
          <div class="whatsapp-templates__table__header__name">Name</div>
          <div class="whatsapp-templates__table__header__category d-none d-md-block">Usage</div>
          <div class="whatsapp-templates__table__header__language d-none d-md-block">Language</div>
          <div class="whatsapp-templates__table__header__status">Status</div>
          <div class="whatsapp-templates__table__header__last-updated d-none d-md-block">Last Updated</div>
          <div class="whatsapp-templates__table__header__actions"></div>
        </div>
        <div class="whatsapp-templates__table__list">
          <div class="whatsapp-templates__table__list__template" v-for="(template, index) of whatsAppTemplates" v-bind:key="template.reference">
            <div class="whatsapp-templates__table__list__template__name">{{template.name}}</div>
            <div class="whatsapp-templates__table__list__template__category d-none d-md-block">{{convertCategoryToUsage(template.category)}}</div>
            <div class="whatsapp-templates__table__list__template__language d-none d-md-block">{{getLanguageNameFromCode(template.language)}}</div>
            <div class="whatsapp-templates__table__list__template__status"
                v-bind:class="{
                    'whatsapp-templates__table__list__template__status--approved' : isTemplateApproved(template),
                    'whatsapp-templates__table__list__template__status--under-review' : isTemplateUnsubmitted(template) || isTemplateReceived(template) || isTemplatePending(template) ,
                    'whatsapp-templates__table__list__template__status--rejected' : isTemplateRejected(template) || isTemplatePaused(template) || isTemplateDisabled(template)}"
                v-html="templateStatusForDisplay(template)">
            </div>
            <div class="whatsapp-templates__table__list__template__last-updated d-none d-md-block">{{friendlyDate(template.lastUpdated)}}</div>
            <div v-on:click.stop="toggleMenu(whatsAppTemplates.length + index)" class="whatsapp-templates__table__list__template__actions">
              <i class="fas fa-ellipsis-h"></i>
              <div v-if="isShowMenu(whatsAppTemplates.length + index)" v-click-outside="hideMenu" class="whatsapp-templates__table__list__template__actions-menu">
                <ul>
                  <li v-if="template.contentSid" v-on:click.stop="editTemplate(template)"><span><i class="fas fa-eye"></i></span> View</li>
                  <li v-else v-on:click.stop="editTemplate(template)"><span><i class="fas fa-edit"></i></span> Edit</li>
                  <li v-on:click.stop="checkIfCanDeleteTemplate(template)"><span><i class="fas fa-trash-alt"></i></span> Delete</li>
                </ul>
              </div>
            </div>
          </div>
          <div v-if="whatsAppTemplates.length === 0" class="whatsapp-templates__list__no-templates">
            You haven't created any WhatsAppTemplates yet
          </div>
          <div v-if="!isWhatsAppActive" class="whatsapp-templates__list__no-templates">
            <span>Please activate WhatsApp. Go to <router-link to="/list-establishments">Configure Establishments</router-link> and click on WhatsApp.</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isWhatsAppActive" class="row mt-3 mb-3">
      <div class="col-12 text-right pr-5">
        <button v-on:click.stop="addTemplate" class="btn btn-primary">Create a Template</button>
      </div>
    </div>

    <edit-template v-if="showEditTemplateDialog" v-bind:template="selectedTemplate"></edit-template>
    <submit-template v-if="showSubmitTemplateDialog" v-bind:template="selectedTemplate"></submit-template>
    <confirm-dialog v-bind:message="confirmDeleteMessage"
                    v-bind:callbackPrimaryName="'delete-template'"
                    v-bind:callbackPrimaryReference="selectedTemplate.reference"
                    v-if="showConfirmDeleteDialog"/>
    <confirm-dialog v-bind:message="cannotDeleteMessage"
                    v-bind:callbackPrimaryName="'close-cannot-delete-dialog'"
                    v-bind:callbackPrimaryReference="'' + _uid"
                    v-bind:continuePrimaryText="'Cancel'"
                    v-bind:hideCancelButton="true"
                    v-if="showCannotDeleteDialog"/>
  </div>
</template>

<script>
    import { store } from '../store.js'
    // import Spinner from '../components/Spinner.vue'

    import EditTemplate from '../components/whatsapp/EditTemplate'
    import SubmitTemplate from '../components/whatsapp/SubmitTemplate'
    import ConfirmDialog from '../components/ConfirmDialog.vue'

    import {datesMixin} from "../mixins/DatesMixin"
    import {languagesMixin} from '../mixins/LanguagesMixin'
    import {whatsAppMixin} from "../mixins/WhatsAppMixin"
    import {whatsAppTemplateMixin} from "../mixins/WhatsAppTemplateMixin"
    import {whatsAppTemplatesMixin} from "../mixins/WhatsAppTemplatesMixin"

    export default {
        name: 'WhatsAppTemplates',
        components: { EditTemplate, SubmitTemplate, ConfirmDialog },
        mixins: [datesMixin, languagesMixin, whatsAppMixin, whatsAppTemplateMixin, whatsAppTemplatesMixin],
        data: function () {
            return {
                menuIndex: null,
                selectedTemplate: null,
                showEditTemplateDialog: false,
                showSubmitTemplateDialog: false,
                showConfirmDeleteDialog: false,
                showCannotDeleteDialog: false,
                cannotDeleteMessage: "",
            }
        },
        computed: {
          confirmDeleteMessage: function() {
              return "Are you sure you want to delete the template: " + (this.selectedTemplate ? this.selectedTemplate.name : "") + "?";
          },
        },
        methods: {
            convertCategoryToUsage: function (category) {
                switch (category) {
                    case "utility":
                        return "Start conversation & Reply";
                    case "marketing":
                        return "Marketing";
                    default:
                        return "Reply only";
                }
            },
            toggleMenu: function(index) {
                if (this.menuIndex === index) {
                    this.menuIndex = null;
                } else {
                    this.menuIndex = index;
                }
            },
            isShowMenu: function(index) {
                return this.menuIndex === index;
            },
            hideMenu: function() {
                console.log('outside');
                this.menuIndex = null;
            },
            addTemplate: function() {
                console.log("Adding a Template");
                this.selectedTemplate = {name: "", category: "utility", language: "en", contentType: "text", content: "", status: "under-review", lastUpdated: null, sampleVariables: [], actions: []};
                this.showEditTemplateDialog = true;
            },
            editTemplate: function (template) {
                console.log("Editing: " + template.reference);
                this.selectedTemplate = template;
                this.showEditTemplateDialog = true;
                this.menuIndex = null;
            },
            stopEditingTemplate(editedTemplate) {
                this.selectedTemplate = null;
                this.showEditTemplateDialog = false;
                if (editedTemplate) {
                    for (let existing of this.whatsAppTemplates) {
                        if (existing.reference === editedTemplate.reference) {
                            Object.assign(existing, editedTemplate);
                            return
                        }
                    }
                    this.whatsAppTemplates.push(editedTemplate);
                }
            },
            checkIfCanDeleteTemplate: function(template) {
                this.$http.get('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/templates/' + template.reference + "/used-by-task-templates"
                ).then(response => {
                    console.log("Automations that use WhatsAppTemplate " + response);
                    if (response.data.length > 0) {
                        this.warnUserAboutTemplateInUse(template, response.data);
                    } else {
                        this.confirmDeleteTemplate(template);
                    }
                }).catch(error => {
                    this.waiting = false
                    if (error.response) {
                        this.error = "Error: " + error.response.data.message
                    } else {
                        this.error = "Sorry, we can't seem to reach our servers! Please try again"
                    }
                })
            },
            warnUserAboutTemplateInUse: function(templateToDelete, automations) {
                this.menuIndex = null;          
                this.cannotDeleteMessage = "You cannot delete this template: " + templateToDelete.name + " as it is currently in use by these automations:<ul>";
                for (let a of automations) {
                    this.cannotDeleteMessage += "<li>" + a + "</li>";
                }
                this.cannotDeleteMessage += "</ul>";
                this.showCannotDeleteDialog = true;
            },
            hideCannotDeleteDialog: function() {
                this.showCannotDeleteDialog = false;
            },
            confirmDeleteTemplate: function(template) {
                this.menuIndex = null;
                this.selectedTemplate = template;
                this.showConfirmDeleteDialog = true;
            },
            deleteWhatsAppTemplate: async function(templateReference) {
                console.log("Deleting WhatsApp Template: " + templateReference);
                this.showConfirmDeleteDialog = false;
                try {
                    this.$http.delete('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/templates/' + templateReference
                    ).then(() => {
                        console.log("WhatsApp Template deleted");
                        // Remove from local list
                        this.whatsAppTemplates = this.whatsAppTemplates.filter(t => t.reference !== templateReference);
                    }).catch(error => {
                        this.waiting = false
                        if (error.response) {
                            this.error = "Error: " + error.response.data.message
                        } else {
                            this.error = "Sorry, we can't seem to reach our servers! Please try again"
                        }
                    })
                } catch (error) {
                    console.error("Error deleting WhatsApp Template: ", error);
                }
            },
            startSubmitTemplate: function (template) {
                console.log("Showing submit dialog: " + template.reference);
                this.selectedTemplate = template;
                this.showEditTemplateDialog = false;
                this.showSubmitTemplateDialog = true;
            },
            stopSubmitTemplate(template) {
                this.selectedTemplate = null;
                this.showSubmitTemplateDialog = false;
                if (template) {
                    for (let existing of this.whatsAppTemplates) {
                        if (existing.reference === template.reference) {
                            Object.assign(existing, template);
                        }
                    }
                }
            },
            loadInitialData: async function() {
                console.log("Loading WhatsApp Templates");
                await this.fetchWhatsApp(store.currentEstablishmentReference);
                this.fetchWhatsAppTemplates();
            },
        },
        mounted: async function() {
            await this.loadInitialData();
            this.$root.$on("finished-editing-template", this.stopEditingTemplate);
            this.$root.$on("start-submitting-template", this.startSubmitTemplate);
            this.$root.$on("finished-submitting-template", this.stopSubmitTemplate);
            this.$root.$on('selected-establishment', this.loadInitialData);
            this.$root.$on('delete-template', this.deleteWhatsAppTemplate);
            this.$root.$on('close-cannot-delete-dialog', this.hideCannotDeleteDialog);
        }, beforeDestroy() {
            this.$root.$off("finished-editing-template", this.stopEditingTemplate);
            this.$root.$off("start-submitting-template", this.startSubmitTemplate);
            this.$root.$off("finished-submitting-template", this.stopSubmitTemplate);
            this.$root.$off('selected-establishment', this.loadInitialData);
            this.$root.$off('delete-template', this.deleteWhatsAppTemplate);
            this.$root.$off('close-cannot-delete-dialog', this.hideCannotDeleteDialog);
        }
    }
</script>

<style lang="scss" scoped>
  @import "../styles/pages/_whatsapp-templates";
</style>
